import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/Button';
import { financeApi } from '@/api/finance.api';
import { showApiError } from '@/utils/apiError';
import type { FinanceHealthCenter, FinanceHealthIssue } from '@/types/finance';

function IssueList({ issues, empty }: { issues: FinanceHealthIssue[]; empty: string }) {
  if (issues.length === 0) {
    return <p className="text-sm text-slate-500">{empty}</p>;
  }
  return (
    <ul className="space-y-3">
      {issues.map((issue) => (
        <li key={`${issue.category}-${issue.title}`} className="rounded-lg border p-3 text-sm">
          <div className="font-medium">{issue.title}</div>
          <p className="text-slate-600 mt-1">{issue.description}</p>
          {issue.actionPath && issue.actionLabel && (
            <Link to={issue.actionPath} className="inline-block mt-2 text-primary-600 text-xs hover:underline">
              {issue.actionLabel} →
            </Link>
          )}
        </li>
      ))}
    </ul>
  );
}

export function FinanceHealthCenterPage() {
  const [health, setHealth] = useState<FinanceHealthCenter | null>(null);

  useEffect(() => {
    financeApi.healthCenter().then(setHealth).catch((e) => showApiError(e, 'Failed to load health center'));
  }, []);

  return (
    <div>
      <PageHeader
        title="Finance health center"
        description="Automatic checks for issues that affect your books, cash, and compliance"
        breadcrumbs={[
          { label: 'Org', to: '/org' },
          { label: 'Finance', to: '/finance' },
          { label: 'Administration', to: '/finance/control' },
          { label: 'Health' },
        ]}
        actions={
          <Button variant="secondary" onClick={() => financeApi.healthCenter().then(setHealth)}>
            Refresh
          </Button>
        }
      />

      {health && (
        <>
          <div className="grid gap-4 sm:grid-cols-3 mb-6">
            <div className="card p-4 text-center">
              <div className="text-3xl font-bold text-primary-600">{health.businessHealthScore}</div>
              <div className="text-sm text-slate-500">Business health score</div>
              <div className="text-xs mt-1 font-medium">{health.healthGrade}</div>
            </div>
            <div className="card p-4 sm:col-span-2">
              <h3 className="font-semibold mb-2">Recommendations</h3>
              <ul className="text-sm space-y-1 list-disc pl-4 text-slate-600">
                {health.recommendations.map((r) => (
                  <li key={r}>{r}</li>
                ))}
              </ul>
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-3">
            <div className="card p-4">
              <h3 className="font-semibold text-red-700 mb-3">Critical</h3>
              <IssueList issues={health.critical} empty="No critical issues" />
            </div>
            <div className="card p-4">
              <h3 className="font-semibold text-amber-700 mb-3">Warnings</h3>
              <IssueList issues={health.warnings} empty="No warnings" />
            </div>
            <div className="card p-4">
              <h3 className="font-semibold text-slate-700 mb-3">Information</h3>
              <IssueList issues={health.information} empty="Nothing to note" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}
